import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClipboardList, LayoutTemplate, Repeat, FileText } from 'lucide-react';
import type { Task } from '../../types/tasks';
import { useTaskStore } from '../../stores/taskStore';
import Spinner from '../common/Spinner';
import TaskFilters from './TaskFilters';
import TaskKanban from './TaskKanban';
import CreateTaskModal from './CreateTaskModal';
import TaskDetailModal from './TaskDetailModal';
import TemplatesTab from './TemplatesTab';
import RecurringTab from './RecurringTab';
import PreInstructionsTab from './PreInstructionsTab';

const TABS = [
  { key: 'tasks', label: 'Tasks', icon: ClipboardList },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'recurring', label: 'Recurring', icon: Repeat },
  { key: 'pre-instructions', label: 'Pre-instructions', icon: FileText },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabKey) || 'tasks';

  const loading = useTaskStore((s) => s.loading);
  const tasks = useTaskStore((s) => s.tasks);
  const fetchTasks = useTaskStore((s) => s.fetchTasks);
  const fetchStats = useTaskStore((s) => s.fetchStats);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [fetchTasks, fetchStats]);

  useEffect(() => {
    if (selectedTask) {
      const fresh = tasks.find((t) => t.id === selectedTask.id);
      if (!fresh) {
        setSelectedTask(null);
      }
    }
  }, [tasks, selectedTask]);

  function setTab(tab: TabKey) {
    if (tab === 'tasks') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Tasks</h1>
        {loading ? <Spinner size="sm" /> : null}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-border-subtle">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                isActive
                  ? 'border-accent-purple text-accent-purple-light'
                  : 'border-transparent text-text-muted hover:text-text-secondary'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'tasks' && (
        <>
          <TaskFilters onNewTask={() => setShowCreate(true)} />
          <TaskKanban onTaskClick={(task) => setSelectedTask(task)} />
        </>
      )}

      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'recurring' && (
        <RecurringTab onTaskClick={(task) => setSelectedTask(task)} />
      )}
      {activeTab === 'pre-instructions' && <PreInstructionsTab />}

      {showCreate ? (
        <CreateTaskModal onClose={() => setShowCreate(false)} />
      ) : null}

      {selectedTask ? (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
        />
      ) : null}
    </div>
  );
}
